import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, BedDouble, Sparkles, Layers, LayoutGrid } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { Reveal } from "@/components/site/Reveal";
import { CountUp } from "@/components/site/CountUp";
import { PROJECTS_GROUPS } from "@/lib/projectsData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=2400&q=85";

const totalProjects = PROJECTS_GROUPS.reduce(
  (sum, g) => sum + g.areas.reduce((a, ar) => a + ar.items.length, 0),
  0
);
const totalAreas = PROJECTS_GROUPS.reduce((s, g) => s + g.areas.length, 0);

export default function Projects() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);

  useEffect(() => {
    document.title =
      "Projects | Holiday Home Furnishing Portfolio — Bloc & Brush Dubai";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section data-testid="projects-hero" className="relative min-h-[80vh] flex items-end overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Dubai skyline" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A33]/30 via-[#0B1A33]/30 to-[#0B1A33]/75" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl pt-32 pb-20 lg:pb-24 text-white">
          <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-xs uppercase tracking-[0.28em] text-white/70">
            Our Projects
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} className="font-display mt-6 text-5xl md:text-6xl lg:text-[72px] font-extrabold leading-[0.98] tracking-tight max-w-5xl">
            A Curated Portfolio
            <br />
            <span className="text-white/65">Of Holiday Homes.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="mt-8 text-base lg:text-[18px] text-white/75 max-w-2xl leading-relaxed">
            From JBR beachfronts to Sharjah's Al Jada — every property below was
            furnished, styled and prepared for guests by the Bloc & Brush team.
            Each listing is live on Airbnb.
          </motion.p>

          <div className="mt-12 grid grid-cols-3 gap-x-6 gap-y-4 max-w-2xl border-t border-white/15 pt-8">
            <div>
              <div className="font-display text-3xl lg:text-4xl font-extrabold">
                <CountUp end={totalProjects} suffix="+" />
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">Properties Furnished</div>
            </div>
            <div>
              <div className="font-display text-3xl lg:text-4xl font-extrabold">
                <CountUp end={totalAreas} />
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">Communities</div>
            </div>
            <div>
              <div className="font-display text-3xl lg:text-4xl font-extrabold">2</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">Emirates</div>
            </div>
          </div>
        </div>
      </section>

      {/* GROUPED PROJECTS */}
      {PROJECTS_GROUPS.map((group, gi) => (
        <section
          key={group.city}
          data-testid={`projects-city-${group.city.toLowerCase()}`}
          className={`${gi % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]"} py-24 lg:py-32`}
        >
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
            <Reveal>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                    Emirate
                  </span>
                  <h2 className="font-display mt-3 text-4xl lg:text-[56px] font-extrabold leading-[1.02] tracking-tight text-[#1C1C1C]">
                    {group.city}.
                  </h2>
                </div>
                <div className="text-[14px] text-[#707070]">
                  {group.areas.reduce((a, ar) => a + ar.items.length, 0)} properties · {group.areas.length} {group.areas.length === 1 ? "community" : "communities"}
                </div>
              </div>
            </Reveal>

            {group.areas.map((area, ai) => (
              <div key={area.area} className="mt-16 lg:mt-20">
                <Reveal>
                  <div className="flex items-center gap-3 mb-8">
                    <MapPin size={16} className="text-[#13294B]" />
                    <h3 className="font-display text-xl lg:text-2xl font-bold text-[#1C1C1C]">
                      {area.area}
                    </h3>
                    <span className="text-[13px] text-[#707070]">· {area.coords}</span>
                  </div>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                  {area.items.map((p, i) => (
                    <Reveal key={p.slug || p.id || i} delay={(i % 3) * 0.06}>
                      <Link
                        to={`/projects/${p.slug || p.id}`}
                        data-testid={`project-card-${gi}-${ai}-${i}`}
                        className="group block bg-white rounded-3xl overflow-hidden border border-black/5 hover:border-[#13294B]/25 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(19,41,75,0.12)] transition-all duration-500"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={p.image}
                            alt={p.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1.4s] ease-out"
                          />
                          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#13294B] tracking-[0.12em] flex items-center gap-1.5">
                            <BedDouble size={12} /> {p.bhk}
                          </div>
                        </div>
                        <div className="p-6 lg:p-7">
                          <div className="text-[11px] uppercase tracking-[0.22em] text-[#707070]">
                            {area.area} · {group.city}
                          </div>
                          <h4 className="font-display mt-2 text-[18px] lg:text-[20px] font-bold text-[#1C1C1C] leading-tight">
                            {p.title}
                          </h4>
                          <p className="mt-3 text-[14px] leading-[1.7] text-[#1C1C1C]/65">
                            {p.description}
                          </p>
                          <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#13294B] group-hover:gap-2 transition-all">
                            View Full Project & Gallery <ArrowUpRight size={14} />
                          </div>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* CTA BAND */}
      <section className="py-20 bg-white border-t border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl lg:text-[40px] font-extrabold tracking-tight text-[#1C1C1C]">
              Want your property featured here next?
            </h2>
            <p className="mt-3 text-[16px] text-[#1C1C1C]/65 leading-relaxed">
              We furnish, style and list holiday homes across the UAE — typically delivered in 2 to 3 weeks.
            </p>
          </div>
          <button
            onClick={() => setQuoteOpen(true)}
            data-testid="projects-cta-btn"
            className="inline-flex items-center gap-2 bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full px-7 py-4 text-[15px] font-medium transition self-start md:self-auto"
          >
            Start your holiday home <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
